import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ClipboardList, FlaskConical, PackageCheck, Pencil, Plus, ShoppingCart, Trash2, Upload } from 'lucide-react';
import { Heading } from '../layout';
import { FormDrawer, PurchaseRequestsForm } from '../forms';
import { Dialog, TabNavigation } from '../reusable';
import RawMaterialsBulkImportModal, {
  downloadRawMaterialsExcelTemplate,
  toRawMaterialApiPayload,
} from './RawMaterialsBulkImportModal';
import { useUIColors } from '../../hooks/useTheme';
import purchaseOrdersAPI from '../../services/purchaseOrdersAPI';
import purchaseRequestsAPI from '../../services/purchaseRequestsAPI';
import suppliersAPI from '../../services/suppliersAPI';
import ProductionDataTable from './ProductionDataTable';
import ProductionStatusChip from './ProductionStatusChip';
import PaintMaterialDetailModal from './PaintMaterialDetailModal';
import PurchaseRequestDetailModal from './PurchaseRequestDetailModal';
import RawMaterialsInventoryChart from './RawMaterialsInventoryChart';
import RawMaterialsMonthEndInventoryModal, {
  formatInventoryPeriod,
  inventoryCountStatusColor,
} from './RawMaterialsMonthEndInventoryModal';
import useProductionStore from './useProductionStore';
import { formatDateEs, formatQty } from './productionDomain';
import { normalizeDepartment, purchaseRequestAreaOptions } from './purchaseRequestAreas';
import {
  notifyPurchaseRequestsChanged,
  PURCHASE_REQUESTS_CHANGED_EVENT,
} from './commercialRequestsUtils';
import {
  emptyTechParam,
  HAZARD_CLASSES,
  MATERIAL_STATUS_COLORS,
  MATERIAL_STATUSES,
  MATERIAL_UNITS,
  STOCK_STATUS_COLORS,
  consolidateMaterials,
  emptyMaterial,
  emptyReception,
  familyForProcurementType,
  familyOptionLabel,
  formatMoneyClp,
  getMaterialCategoryPrefixMap,
  getMaterialFamilies,
  getTechParamSuggestions,
  inventoryValueOf,
  isMaterialCode,
  isPendingReception,
  lotsForMaterial,
  measuredSpecStatus,
  MP_WAREHOUSES,
  batchParamsFromMaterial,
  nextLotCode,
  nextMaterialCode,
  procurementOptionsFromCatalog,
  RAW_MATERIALS_CATALOGS_CHANGED_EVENT,
  sanitizeTechParams,
  techParamRangeError,
  upsertMaterialCategory,
  upsertTechParamSuggestion,
} from './rawMaterialsDomain';

const PRODUCTION_PURCHASE_AREA = 'Producción';
const PURCHASE_CART_KEY = 'sqmt.production.raw-materials.purchase-cart.v1';

const PURCHASE_REQUEST_STATUS_COLORS = {
  Borrador: '#64748b',
  Enviada: '#2563eb',
  Aprobada: '#059669',
  Rechazada: '#dc2626',
  'Convertida a OC': '#7c3aed',
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const readPurchaseCart = () => {
  try {
    const raw = window.localStorage.getItem(PURCHASE_CART_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writePurchaseCart = (rows) => {
  try {
    window.localStorage.setItem(PURCHASE_CART_KEY, JSON.stringify(rows || []));
  } catch {
    /* ignore quota / private mode */
  }
};

const emptyPurchaseRequest = () => ({
  request_code: '',
  requester_name: '',
  department: PRODUCTION_PURCHASE_AREA,
  supplier_id: '',
  request_date: todayIso(),
  needed_by_date: '',
  priority: 'Normal',
  status: 'Borrador',
  justification: '',
  notes: '',
  items: [{ id: null, item_code: '', description: '', quantity: 1, unit: 'UN', estimated_unit_price: 0 }],
});

const cell = (children, align = 'left', extra = {}) => (
  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: align, ...extra }}>{children}</td>
);

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 36,
  padding: '0 12px',
  borderRadius: 8,
  border: 'none',
  background: '#16a34a',
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
};

const iconBtn = (ui) => ({
  height: 28,
  width: 28,
  borderRadius: 6,
  border: `1px solid ${ui.border}`,
  background: 'transparent',
  color: ui.text,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const cellInput = (baseInputStyle) => ({
  ...baseInputStyle,
  marginTop: 0,
  height: 32,
  fontSize: 12,
});

const TechParamsTable = ({
  rows = [],
  onChange,
  colors,
  baseInputStyle,
  suggestions = [],
  onSuggestionsChange,
}) => {
  const listId = 'mp-tech-param-suggestions';
  const [showNewParam, setShowNewParam] = useState(false);
  const [newParamName, setNewParamName] = useState('');
  const [newParamUnit, setNewParamUnit] = useState('');
  const [paramHint, setParamHint] = useState('');

  const updateRow = (index, patch) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };
  const applySuggestion = (index, name) => {
    const preset = suggestions.find((item) => item.name === name);
    updateRow(index, { name, unit: preset?.unit ?? rows[index]?.unit ?? '' });
  };
  const persistSuggestion = (name, unit) => {
    const result = upsertTechParamSuggestion({ name, unit });
    if (!result.ok) {
      setParamHint(result.error || 'No se pudo guardar el parámetro.');
      return result;
    }
    onSuggestionsChange?.();
    setParamHint(
      result.existing
        ? `“${result.name}” ya estaba en la lista.`
        : `“${result.name}” se agregó a la lista desplegable.`
    );
    return result;
  };
  const handleCreateParamType = () => {
    const result = persistSuggestion(newParamName, newParamUnit);
    if (!result.ok) return;
    onChange([
      ...rows,
      {
        ...emptyTechParam(),
        name: result.name,
        unit: result.unit || newParamUnit || '',
      },
    ]);
    setNewParamName('');
    setNewParamUnit('');
    setShowNewParam(false);
  };

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <datalist id={listId}>
        {suggestions.map((item) => (
          <option key={item.name} value={item.name} />
        ))}
      </datalist>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
          <thead>
            <tr>
              {['Parámetro', 'Unidad', 'Valor ideal', 'Mínimo', 'Máximo', ''].map((header) => (
                <th
                  key={header || 'actions'}
                  style={{
                    textAlign: header ? 'left' : 'right',
                    padding: '6px 8px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.muted,
                    borderBottom: `1px solid ${colors.sectionBorder}`,
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((row, index) => (
                <tr key={row.key || index}>
                  <td style={{ padding: '6px 6px 6px 0' }}>
                    <input
                      value={row.name || ''}
                      list={listId}
                      placeholder="Densidad, VOC…"
                      onChange={(event) => applySuggestion(index, event.target.value)}
                      onBlur={() => {
                        if (String(row.name || '').trim()) {
                          persistSuggestion(row.name, row.unit);
                        }
                      }}
                      style={cellInput(baseInputStyle)}
                    />
                  </td>
                  <td style={{ padding: 6, width: 88 }}>
                    <input
                      value={row.unit || ''}
                      placeholder="g/cm³"
                      onChange={(event) => updateRow(index, { unit: event.target.value })}
                      onBlur={() => {
                        if (String(row.name || '').trim()) {
                          persistSuggestion(row.name, row.unit);
                        }
                      }}
                      style={cellInput(baseInputStyle)}
                    />
                  </td>
                  <td style={{ padding: 6, width: 100 }}>
                    <input
                      value={row.ideal ?? ''}
                      placeholder="—"
                      onChange={(event) => updateRow(index, { ideal: event.target.value })}
                      style={cellInput(baseInputStyle)}
                    />
                  </td>
                  <td style={{ padding: 6, width: 88 }}>
                    <input
                      value={row.min ?? ''}
                      placeholder="—"
                      onChange={(event) => updateRow(index, { min: event.target.value })}
                      style={cellInput(baseInputStyle)}
                    />
                  </td>
                  <td style={{ padding: 6, width: 88 }}>
                    <input
                      value={row.max ?? ''}
                      placeholder="—"
                      onChange={(event) => updateRow(index, { max: event.target.value })}
                      style={cellInput(baseInputStyle)}
                    />
                  </td>
                  <td style={{ padding: '6px 0 6px 6px', width: 36, textAlign: 'right' }}>
                    <button
                      type="button"
                      aria-label="Quitar parámetro"
                      onClick={() => onChange(rows.filter((_, i) => i !== index))}
                      style={{
                        height: 32,
                        width: 32,
                        border: 'none',
                        background: 'transparent',
                        color: colors.danger || '#dc2626',
                        cursor: 'pointer',
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} style={{ padding: '14px 8px', fontSize: 12, color: colors.muted }}>
                  Sin parámetros. Agrega densidad, sólidos, VOC u otros rangos de control.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <button
          type="button"
          onClick={() => onChange([...rows, emptyTechParam()])}
          style={{
            ...primaryBtn,
            height: 32,
            background: 'transparent',
            color: colors.text,
            border: `1px solid ${colors.inputBorder}`,
          }}
        >
          <Plus size={14} /> Agregar Fila
        </button>
        <button
          type="button"
          onClick={() => {
            setShowNewParam((prev) => !prev);
            setParamHint('');
          }}
          style={{
            ...primaryBtn,
            height: 32,
            background: 'transparent',
            color: colors.text,
            border: `1px solid ${colors.inputBorder}`,
          }}
        >
          <Plus size={14} /> Agregar Parámetro
        </button>
      </div>
      {showNewParam ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.4fr) minmax(90px, 0.6fr) auto',
            gap: 8,
            alignItems: 'end',
            padding: 10,
            borderRadius: 8,
            border: `1px solid ${colors.inputBorder}`,
            background: colors.panel || 'transparent',
          }}
        >
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: colors.muted }}>Nombre del parámetro</span>
            <input
              value={newParamName}
              onChange={(event) => setNewParamName(event.target.value)}
              placeholder="Ej. pH, Humedad…"
              style={cellInput(baseInputStyle)}
            />
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: colors.muted }}>Unidad</span>
            <input
              value={newParamUnit}
              onChange={(event) => setNewParamUnit(event.target.value)}
              placeholder="—, %, °C…"
              style={cellInput(baseInputStyle)}
            />
          </label>
          <button type="button" onClick={handleCreateParamType} style={{ ...primaryBtn, height: 32 }}>
            Guardar en lista
          </button>
        </div>
      ) : null}
      {paramHint ? (
        <p style={{ margin: 0, fontSize: 11, color: colors.muted }}>{paramHint}</p>
      ) : null}
    </div>
  );
};

const FamilySelectField = ({
  value,
  onChange,
  colors,
  baseInputStyle,
  families = [],
  onCatalogChange,
  isDarkMode = false,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPrefix, setNewPrefix] = useState('');
  const [createError, setCreateError] = useState('');
  const prefixMap = getMaterialCategoryPrefixMap();

  const openModal = () => {
    setNewName('');
    setNewPrefix('');
    setCreateError('');
    setShowModal(true);
  };

  const handleCreate = () => {
    const result = upsertMaterialCategory({ name: newName, prefix: newPrefix });
    if (!result.ok) {
      setCreateError(result.error || 'No se pudo crear la categoría.');
      return;
    }
    onCatalogChange?.();
    onChange(result.name);
    setShowModal(false);
    setNewName('');
    setNewPrefix('');
    setCreateError('');
  };

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
        <select
          value={value || ''}
          onChange={(event) => onChange(event.target.value)}
          style={{ ...baseInputStyle, flex: 1, minWidth: 0 }}
        >
          {families.map((item) => (
            <option key={item} value={item}>
              {familyOptionLabel(item)}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={openModal}
          title="Nueva categoría"
          aria-label="Nueva categoría"
          style={{
            width: 36,
            height: baseInputStyle?.height || 36,
            flexShrink: 0,
            borderRadius: 8,
            border: `1px solid ${colors.inputBorder}`,
            background: isDarkMode ? '#27272a' : '#f3f4f6',
            color: colors.text,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
        >
          <Plus size={16} />
        </button>
      </div>
      {value && prefixMap[value] ? (
        <p style={{ margin: 0, fontSize: 11, color: colors.muted }}>
          Códigos: {prefixMap[value]}0001, {prefixMap[value]}0002…
        </p>
      ) : null}

      <Dialog
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Nueva categoría"
        subtitle="Se agrega al catálogo de materias primas y queda disponible en el desplegable."
        size="md"
        isDarkMode={isDarkMode}
        zIndex={10050}
      >
        <div style={{ display: 'grid', gap: 14 }}>
          <label style={{ color: colors.text, fontSize: 13, display: 'grid', gap: 6 }}>
            Nombre de la categoría
            <input
              style={baseInputStyle}
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder="Ej. Catalizadores"
              autoFocus
            />
          </label>
          <label style={{ color: colors.text, fontSize: 13, display: 'grid', gap: 6 }}>
            Prefijo del código (2 letras)
            <input
              style={{ ...baseInputStyle, textTransform: 'uppercase', maxWidth: 120 }}
              value={newPrefix}
              maxLength={2}
              onChange={(event) =>
                setNewPrefix(
                  event.target.value
                    .replace(/[^a-zA-Z]/g, '')
                    .toUpperCase()
                    .slice(0, 2)
                )
              }
              placeholder="CT"
            />
          </label>
          {newPrefix.length === 2 ? (
            <p style={{ margin: 0, fontSize: 12, color: colors.muted }}>
              Los códigos serán {newPrefix}0001, {newPrefix}0002…
            </p>
          ) : null}
          {createError ? (
            <p style={{ margin: 0, fontSize: 12, color: colors.danger || '#dc2626' }}>{createError}</p>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
            <button
              type="button"
              onClick={() => setShowModal(false)}
              style={{
                height: 36,
                borderRadius: 8,
                border: `1px solid ${colors.inputBorder}`,
                background: 'transparent',
                color: colors.text,
                padding: '0 14px',
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleCreate}
              style={{
                height: 36,
                borderRadius: 8,
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                padding: '0 16px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Crear categoría
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

const BatchParamsTable = ({ rows = [], onChange, colors, baseInputStyle }) => {
  const updateMeasured = (index, measured) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, measured } : row)));
  };
  const specLabel = (row) => {
    const min = row.min === '' || row.min == null ? '' : String(row.min);
    const max = row.max === '' || row.max == null ? '' : String(row.max);
    const ideal = row.ideal === '' || row.ideal == null ? '' : String(row.ideal);
    if (min && max) return `${min} – ${max}${ideal ? ` · ideal ${ideal}` : ''}`;
    if (ideal) return `Ideal ${ideal}`;
    return '—';
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
        <thead>
          <tr>
            {['Parámetro', 'Unidad', 'Especificación ficha', 'Valor del lote'].map((header) => (
              <th
                key={header}
                style={{
                  textAlign: 'left',
                  padding: '6px 8px',
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.muted,
                  borderBottom: `1px solid ${colors.sectionBorder}`,
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((row, index) => {
              const status = measuredSpecStatus(row);
              const alert = status === 'low' || status === 'high';
              return (
                <tr key={row.key || row.name || index}>
                  <td style={{ padding: '8px 8px 8px 0', fontSize: 13 }}>{row.name}</td>
                  <td style={{ padding: 8, fontSize: 13, color: colors.muted, width: 80 }}>{row.unit || '—'}</td>
                  <td style={{ padding: 8, fontSize: 12, color: colors.muted }}>{specLabel(row)}</td>
                  <td style={{ padding: '8px 0', width: 140 }}>
                    <input
                      value={row.measured ?? ''}
                      placeholder="Medido"
                      onChange={(event) => updateMeasured(index, event.target.value)}
                      style={{
                        ...cellInput(baseInputStyle),
                        borderColor: alert ? '#dc2626' : baseInputStyle.borderColor,
                        color: alert ? '#dc2626' : baseInputStyle.color,
                      }}
                    />
                    {alert ? (
                      <div style={{ marginTop: 4, fontSize: 11, color: '#dc2626' }}>
                        Fuera de especificación
                      </div>
                    ) : null}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={4} style={{ padding: '14px 8px', fontSize: 12, color: colors.muted }}>
                Esta ficha no tiene parámetros técnicos. Agrégalos en el catálogo para controlar el lote.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const MaterialsTable = ({
  materials,
  lots,
  receptions,
  ingresos,
  egresos,
  inventoryCounts = [],
  catalogByCode,
  onCreate,
  onBulkImport,
  onBulkImportTemplate,
  onEdit,
  onOpen,
  onReceive,
  onCreateReception,
  onCreateSkuFromReception,
  onCreateInventoryCount,
  onOpenInventoryCount,
}) => {
  const ui = useUIColors();
  const [view, setView] = useState('consolidado');
  const [family, setFamily] = useState('all');
  const familyOf = useMemo(
    () => Object.fromEntries(materials.map((row) => [row.code, row.family])),
    [materials]
  );
  const matchFamily = (code) => family === 'all' || familyOf[code] === family;
  const matchReceptionFamily = (row) =>
    family === 'all' || !row.material_code || familyOf[row.material_code] === family;

  const consolidado = useMemo(
    () =>
      consolidateMaterials(materials, lots)
        .filter((row) => family === 'all' || row.family === family)
        .map((row) => ({
          ...row,
          inventory_value: inventoryValueOf(row.stock_qty, row, catalogByCode),
        })),
    [materials, lots, family, catalogByCode]
  );

  const nameByCode = useMemo(
    () => Object.fromEntries(materials.map((row) => [row.code, row.name])),
    [materials]
  );

  const detalle = lots
    .filter((lot) => matchFamily(lot.material_code))
    .map((lot) => ({
      ...lot,
      material_name: nameByCode[lot.material_code] || lot.material_code,
    }));

  const pendingReceptions = receptions.filter(
    (row) => matchReceptionFamily(row) && isPendingReception(row)
  );

  const tabs = [
    { key: 'consolidado', label: 'Consolidado', count: consolidado.length },
    { key: 'detalle', label: 'Detalle', count: detalle.length },
    { key: 'recepcion', label: 'Recepción', count: pendingReceptions.length },
    { key: 'ingreso', label: 'Ingreso', count: ingresos.filter((row) => matchFamily(row.material_code)).length },
    { key: 'egresos', label: 'Egresos', count: egresos.filter((row) => matchFamily(row.material_code)).length },
    { key: 'cierre', label: 'Cierre de mes', count: inventoryCounts.length },
  ];

  const views = {
    consolidado: {
      title: 'Materias primas',
      subtitle: `${consolidado.length} ítems`,
      rows: consolidado,
      searchPlaceholder: 'Buscar código, adquisiciones, nombre, CAS…',
      defaultSortKey: 'code',
      onRowClick: onOpen,
      action: (
        <div style={{ display: 'inline-flex', alignItems: 'stretch', gap: 8 }}>
          <button type="button" onClick={() => onCreate(family === 'all' ? 'Resinas' : family)} style={primaryBtn}>
            <Plus size={14} /> Nueva materia prima
          </button>
          {onBulkImport ? (
            <div style={{ display: 'inline-flex', alignItems: 'stretch' }}>
              <button
                type="button"
                onClick={onBulkImport}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 36,
                  padding: '0 12px',
                  borderRadius: onBulkImportTemplate ? '8px 0 0 8px' : 8,
                  border: `1px solid ${ui.inputBorder}`,
                  borderRight: onBulkImportTemplate ? 'none' : `1px solid ${ui.inputBorder}`,
                  background: ui.isDarkMode ? '#27272a' : '#fff',
                  color: ui.text,
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                <Upload size={16} /> Carga masiva
              </button>
              {onBulkImportTemplate ? (
                <button
                  type="button"
                  onClick={onBulkImportTemplate}
                  title="Descargar plantilla Excel"
                  aria-label="Descargar plantilla Excel"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 34,
                    height: 36,
                    padding: 0,
                    borderRadius: '0 8px 8px 0',
                    border: `1px solid ${ui.inputBorder}`,
                    background: ui.isDarkMode ? '#3f3f46' : '#f8fafc',
                    color: ui.text,
                    cursor: 'pointer',
                  }}
                >
                  <Upload size={14} />
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ),
      columns: [
        { key: 'Código producción', sortKey: 'code', align: 'left' },
        { key: 'Cód. adquisiciones', sortKey: 'procurement_code', align: 'left' },
        { key: 'Nombre', sortKey: 'name', align: 'left' },
        { key: 'Categoría', sortKey: 'family', align: 'left' },
        { key: 'CAS', sortKey: 'cas_number', align: 'left' },
        { key: 'Stock', sortKey: 'stock_qty', align: 'right' },
        { key: 'Mínimo', sortKey: 'min_stock', align: 'right' },
        { key: 'Valor inventario', sortKey: 'inventory_value', align: 'right' },
        { key: 'Bodegas', sortKey: 'warehouses', align: 'left' },
        { key: 'Peligro', sortKey: 'hazard_class', align: 'left' },
        { key: 'Estado ficha', sortKey: 'status', align: 'center' },
        { key: 'Estado stock', sortKey: 'stock_status', align: 'center' },
        { key: '', sortKey: null, align: 'right' },
      ],
      renderCells: (row, colors) => (
        <>
          {cell(<strong style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '0.04em' }}>{row.code}</strong>)}
          {cell(row.procurement_code || '—', 'left', { color: row.procurement_code ? colors.text : colors.muted })}
          {cell(row.name)}
          {cell(familyOptionLabel(row.family), 'left', { color: colors.muted })}
          {cell(row.cas_number || '—')}
          {cell(formatQty(row.stock_qty, row.unit), 'right')}
          {cell(formatQty(row.min_stock, row.unit), 'right')}
          {cell(formatMoneyClp(row.inventory_value), 'right')}
          {cell(row.warehouses)}
          {cell(row.hazard_class)}
          {cell(
            <ProductionStatusChip status={row.status} color={MATERIAL_STATUS_COLORS[row.status]} />,
            'center'
          )}
          {cell(
            <ProductionStatusChip status={row.stock_status} color={STOCK_STATUS_COLORS[row.stock_status]} />,
            'center'
          )}
          {cell(
            <span style={{ display: 'inline-flex', gap: 4, justifyContent: 'flex-end' }}>
              <button
                type="button"
                title="Recepcionar"
                style={iconBtn(colors)}
                onClick={(event) => {
                  event.stopPropagation();
                  onReceive?.(row);
                }}
              >
                <PackageCheck size={13} />
              </button>
              <button
                type="button"
                title="Editar"
                style={iconBtn(colors)}
                onClick={(event) => {
                  event.stopPropagation();
                  onEdit(row);
                }}
              >
                <Pencil size={13} />
              </button>
            </span>,
            'right'
          )}
        </>
      ),
    },
    detalle: {
      title: 'Detalle por lote y bodega',
      rows: detalle,
      searchPlaceholder: 'Buscar lote, SKU, bodega…',
      defaultSortKey: 'received_at',
      columns: [
        { key: 'Lote interno', sortKey: 'lot_code' },
        { key: 'Código', sortKey: 'material_code' },
        { key: 'Materia prima', sortKey: 'material_name' },
        { key: 'Bodega', sortKey: 'warehouse' },
        { key: 'Cantidad', sortKey: 'quantity', align: 'right' },
        { key: 'Lote proveedor', sortKey: 'supplier_lot' },
        { key: 'Recepción', sortKey: 'received_at' },
        { key: 'Vence', sortKey: 'expiry' },
        { key: 'Estado', sortKey: 'status', align: 'center' },
      ],
      renderCells: (row) => (
        <>
          {cell(<strong>{row.lot_code}</strong>)}
          {cell(row.material_code)}
          {cell(row.material_name)}
          {cell(row.warehouse)}
          {cell(formatQty(row.quantity, row.unit), 'right')}
          {cell(row.supplier_lot)}
          {cell(formatDateEs(row.received_at))}
          {cell(formatDateEs(row.expiry))}
          {cell(row.status, 'center')}
        </>
      ),
    },
    recepcion: {
      title: 'Recepciones pendientes de ingreso',
      rows: pendingReceptions,
      searchPlaceholder: 'Buscar recepción, OC, código ADQ…',
      defaultSortKey: 'date',
      action: (
        <button type="button" onClick={onCreateReception} style={primaryBtn}>
          <PackageCheck size={14} /> Nueva recepción
        </button>
      ),
      columns: [
        { key: 'Código', sortKey: 'code' },
        { key: 'Fecha', sortKey: 'date' },
        { key: 'OC', sortKey: 'oc_code' },
        { key: 'Cód. adquisiciones', sortKey: 'procurement_code' },
        { key: 'SKU producción', sortKey: 'material_code' },
        { key: 'Materia prima', sortKey: 'material_name' },
        { key: 'Cantidad', sortKey: 'quantity', align: 'right' },
        { key: 'Bodega', sortKey: 'warehouse' },
        { key: 'Proveedor', sortKey: 'supplier' },
        { key: 'Estado', sortKey: 'status', align: 'center' },
        { key: '', sortKey: null, align: 'right' },
      ],
      renderCells: (row, colors) => {
        const needsSku = Boolean(row.needs_production_sku) || !row.material_code;
        return (
          <>
            {cell(<strong>{row.code}</strong>)}
            {cell(formatDateEs(row.date))}
            {cell(row.oc_code)}
            {cell(
              <strong style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '0.04em' }}>
                {row.procurement_code || '—'}
              </strong>
            )}
            {cell(
              row.material_code || '—',
              'left',
              { color: row.material_code ? colors.text : colors.muted }
            )}
            {cell(row.material_name || row.procurement_name || '—')}
            {cell(formatQty(row.quantity, row.unit), 'right')}
            {cell(row.warehouse)}
            {cell(row.supplier)}
            {cell(
              <ProductionStatusChip status={row.status} color="#d97706" />,
              'center'
            )}
            {cell(
              <button
                type="button"
                title={
                  needsSku
                    ? 'Crear SKU de producción para recepcionar'
                    : 'Recepcionar e ingresar'
                }
                style={iconBtn(colors)}
                onClick={(event) => {
                  event.stopPropagation();
                  if (needsSku) onCreateSkuFromReception?.(row);
                  else onReceive?.(row);
                }}
              >
                {needsSku ? <Plus size={13} /> : <PackageCheck size={13} />}
              </button>,
              'right'
            )}
          </>
        );
      },
    },
    ingreso: {
      title: 'Ingresos de inventario',
      rows: ingresos.filter((row) => matchFamily(row.material_code)),
      searchPlaceholder: 'Buscar ingreso, origen, SKU…',
      defaultSortKey: 'date',
      columns: [
        { key: 'Código', sortKey: 'code' },
        { key: 'Fecha', sortKey: 'date' },
        { key: 'SKU', sortKey: 'material_code' },
        { key: 'Materia prima', sortKey: 'material_name' },
        { key: 'Cantidad', sortKey: 'quantity', align: 'right' },
        { key: 'Bodega', sortKey: 'warehouse' },
        { key: 'Origen', sortKey: 'origin' },
        { key: 'Referencia', sortKey: 'reference' },
        { key: 'Estado', sortKey: 'status', align: 'center' },
      ],
      renderCells: (row) => (
        <>
          {cell(<strong>{row.code}</strong>)}
          {cell(formatDateEs(row.date))}
          {cell(row.material_code)}
          {cell(row.material_name)}
          {cell(formatQty(row.quantity, row.unit), 'right')}
          {cell(row.warehouse)}
          {cell(row.origin)}
          {cell(row.reference)}
          {cell(row.status, 'center')}
        </>
      ),
    },
    egresos: {
      title: 'Egresos de inventario',
      rows: egresos.filter((row) => matchFamily(row.material_code)),
      searchPlaceholder: 'Buscar egreso, destino, OP…',
      defaultSortKey: 'date',
      columns: [
        { key: 'Código', sortKey: 'code' },
        { key: 'Fecha', sortKey: 'date' },
        { key: 'SKU', sortKey: 'material_code' },
        { key: 'Materia prima', sortKey: 'material_name' },
        { key: 'Cantidad', sortKey: 'quantity', align: 'right' },
        { key: 'Bodega', sortKey: 'warehouse' },
        { key: 'Destino', sortKey: 'destination' },
        { key: 'Referencia', sortKey: 'reference' },
        { key: 'Estado', sortKey: 'status', align: 'center' },
      ],
      renderCells: (row) => (
        <>
          {cell(<strong>{row.code}</strong>)}
          {cell(formatDateEs(row.date))}
          {cell(row.material_code)}
          {cell(row.material_name)}
          {cell(formatQty(row.quantity, row.unit), 'right')}
          {cell(row.warehouse)}
          {cell(row.destination)}
          {cell(row.reference)}
          {cell(row.status, 'center')}
        </>
      ),
    },
    cierre: {
      title: 'Inventario de cierre de mes',
      subtitle: 'Punto de partida del mes siguiente',
      rows: inventoryCounts,
      searchPlaceholder: 'Buscar mes, código, estado…',
      defaultSortKey: 'period',
      defaultSortDir: 'desc',
      onRowClick: onOpenInventoryCount,
      action: (
        <button type="button" onClick={onCreateInventoryCount} style={primaryBtn}>
          <ClipboardList size={14} /> Nuevo inventario de cierre
        </button>
      ),
      columns: [
        { key: 'Código', sortKey: 'code' },
        { key: 'Mes', sortKey: 'period' },
        { key: 'Fecha conteo', sortKey: 'count_date' },
        { key: 'Apertura de', sortKey: 'opening_period' },
        { key: 'SKU', sortKey: 'items_count', align: 'right' },
        { key: 'Teórico', sortKey: 'theoretical_qty', align: 'right' },
        { key: 'Contado', sortKey: 'counted_qty', align: 'right' },
        { key: 'Diferencia', sortKey: 'difference_qty', align: 'right' },
        { key: 'Estado', sortKey: 'status', align: 'center' },
      ],
      renderCells: (row) => (
        <>
          {cell(<strong>{row.code}</strong>)}
          {cell(formatInventoryPeriod(row))}
          {cell(formatDateEs(row.count_date))}
          {cell(row.opening_period_label || row.opening_period || '—')}
          {cell(String(row.items_count || 0), 'right')}
          {cell(formatQty(row.theoretical_qty), 'right')}
          {cell(formatQty(row.counted_qty), 'right')}
          {cell(formatQty(row.difference_qty), 'right')}
          {cell(
            <ProductionStatusChip status={row.status} color={inventoryCountStatusColor[row.status]} />,
            'center'
          )}
        </>
      ),
    },
  };

  const current = views[view] || views.consolidado;

  return (
    <ProductionDataTable
      title={current.title}
      subtitle={current.subtitle}
      rows={current.rows}
      columns={current.columns}
      renderCells={current.renderCells}
      searchPlaceholder={current.searchPlaceholder}
      defaultSortKey={current.defaultSortKey}
      defaultSortDir={current.defaultSortDir || 'asc'}
      onRowClick={current.onRowClick}
      action={current.action}
      tabs={tabs}
      activeTab={view}
      onTabChange={setView}
      toolbarExtra={
        view === 'cierre' ? null : (
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            fontSize: 11,
            fontWeight: 600,
            color: ui.muted,
          }}
        >
          Categoría
          <select
            value={family}
            onChange={(event) => setFamily(event.target.value)}
            aria-label="Filtrar por categoría"
            style={{
              height: 36,
              borderRadius: 8,
              border: `1px solid ${ui.inputBorder}`,
              background: ui.inputBg,
              color: ui.text,
              padding: '0 10px',
              fontSize: 13,
              minWidth: 180,
            }}
          >
            <option value="all">Todas las categorías</option>
            {getMaterialFamilies().map((item) => (
              <option key={item} value={item}>
                {familyOptionLabel(item)}
              </option>
            ))}
          </select>
        </label>
        )
      }
    />
  );
};


const PaintRawMaterialsWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const {
    materials,
    lots,
    receptions,
    ingresos,
    egresos,
    inventoryCounts,
    saveMaterialRecord,
    saveReceptionRecord,
    refreshRemote,
  } = useProductionStore();
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(emptyMaterial());
  const [formError, setFormError] = useState('');
  const [showReception, setShowReception] = useState(false);
  const [isSavingReception, setIsSavingReception] = useState(false);
  const [receptionForm, setReceptionForm] = useState(emptyReception());
  const [receptionError, setReceptionError] = useState('');
  const [detail, setDetail] = useState(null);
  const [catalogItems, setCatalogItems] = useState([]);
  const [purchaseRequests, setPurchaseRequests] = useState([]);
  const [purchaseRequestItems, setPurchaseRequestItems] = useState([]);
  const [comprasView, setComprasView] = useState('solicitudes');
  const [purchaseCart, setPurchaseCart] = useState(() => readPurchaseCart());
  const [cartMaterialCode, setCartMaterialCode] = useState('');
  const [cartQuantity, setCartQuantity] = useState('1');
  const [suppliers, setSuppliers] = useState([]);
  const [showPurchaseRequest, setShowPurchaseRequest] = useState(false);
  const [isEditingPurchaseRequest, setIsEditingPurchaseRequest] = useState(false);
  const [editingPurchaseRequestId, setEditingPurchaseRequestId] = useState(null);
  const [purchaseRequestFromCart, setPurchaseRequestFromCart] = useState(false);
  const [purchaseRequestForm, setPurchaseRequestForm] = useState(emptyPurchaseRequest());
  const [purchaseRequestError, setPurchaseRequestError] = useState('');
  const [isSavingPurchaseRequest, setIsSavingPurchaseRequest] = useState(false);
  const [purchaseRequestDetail, setPurchaseRequestDetail] = useState(null);
  const [showInventoryCount, setShowInventoryCount] = useState(false);
  const [inventoryCountId, setInventoryCountId] = useState(null);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [isImportingMaterials, setIsImportingMaterials] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState(null);
  const [bulkImportNotice, setBulkImportNotice] = useState('');
  const [catalogTick, setCatalogTick] = useState(0);

  const materialFamilies = useMemo(() => getMaterialFamilies(), [catalogTick]);
  const techParamSuggestions = useMemo(() => getTechParamSuggestions(), [catalogTick]);

  useEffect(() => {
    const refresh = () => setCatalogTick((n) => n + 1);
    window.addEventListener(RAW_MATERIALS_CATALOGS_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(RAW_MATERIALS_CATALOGS_CHANGED_EVENT, refresh);
  }, []);

  const persistPurchaseCart = useCallback((rows) => {
    setPurchaseCart(rows);
    writePurchaseCart(rows);
  }, []);

  const loadPurchaseRequests = useCallback(async () => {
    try {
      const [requestsData, itemsData] = await Promise.all([
        purchaseRequestsAPI.getAll(),
        purchaseRequestsAPI.getAllItems(),
      ]);
      const requests = Array.isArray(requestsData) ? requestsData : [];
      const items = Array.isArray(itemsData) ? itemsData : [];
      const area = PRODUCTION_PURCHASE_AREA.toLowerCase();
      setPurchaseRequests(
        requests.filter((row) => normalizeDepartment(row.department).toLowerCase() === area)
      );
      setPurchaseRequestItems(
        items.filter((row) => normalizeDepartment(row.department).toLowerCase() === area)
      );
    } catch (err) {
      console.error(err);
      setPurchaseRequests([]);
      setPurchaseRequestItems([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    purchaseOrdersAPI
      .getCatalog()
      .then((data) => {
        if (!cancelled) setCatalogItems(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setCatalogItems([]);
      });
    suppliersAPI
      .getAll()
      .then((data) => {
        if (!cancelled) setSuppliers(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setSuppliers([]);
      });
    loadPurchaseRequests();
    return () => {
      cancelled = true;
    };
  }, [loadPurchaseRequests]);

  useEffect(() => {
    const refresh = () => {
      loadPurchaseRequests();
    };
    window.addEventListener(PURCHASE_REQUESTS_CHANGED_EVENT, refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener(PURCHASE_REQUESTS_CHANGED_EVENT, refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [loadPurchaseRequests]);

  const procurementOptions = useMemo(
    () =>
      [
        { value: '', label: 'Seleccionar ítem de adquisiciones…', name: '', item_type: '' },
        ...procurementOptionsFromCatalog(
          catalogItems,
          materials,
          formData.procurement_code,
          formData.family
        ),
      ],
    [catalogItems, materials, formData.procurement_code, formData.family]
  );

  const catalogByCode = useMemo(
    () =>
      Object.fromEntries(
        (catalogItems || []).map((item) => [String(item.item_code || item.sku || '').trim().toUpperCase(), item])
      ),
    [catalogItems]
  );

  const supplierOptions = useMemo(
    () =>
      suppliers.map((s) => ({
        value: String(s.id),
        label: `${s.supplier_code || ''} · ${s.name}`.trim(),
      })),
    [suppliers]
  );

  const purchaseRequestAreaOpts = useMemo(
    () => purchaseRequestAreaOptions([PRODUCTION_PURCHASE_AREA]),
    []
  );

  const openCreate = (initialFamily = 'Resinas') => {
    const map = getMaterialCategoryPrefixMap();
    const family = map[initialFamily] ? initialFamily : 'Resinas';
    setFormData({
      ...emptyMaterial(),
      family,
      code: nextMaterialCode(materials, family),
      tech_params: [emptyTechParam()],
    });
    setIsEditing(false);
    setFormError('');
    setShowForm(true);
    setDetail(null);
  };

  const handleBulkImportMaterials = async (rows) => {
    setIsImportingMaterials(true);
    setBulkImportResult(null);
    const errorRows = [];
    let created = 0;
    let skipped = 0;
    let working = [...(materials || [])];
    try {
      for (let index = 0; index < (rows || []).length; index += 1) {
        const row = rows[index];
        try {
          const payload = toRawMaterialApiPayload(row, working);
          const duplicate = working.some(
            (item) => String(item.code || '').toUpperCase() === String(payload.code || '').toUpperCase()
          );
          if (duplicate) {
            skipped += 1;
            continue;
          }
          await saveMaterialRecord(payload, null);
          created += 1;
          working = [...working, { ...payload, id: `tmp-${created}` }];
        } catch (err) {
          errorRows.push({
            row: index + 1,
            detail: err?.message || 'No se pudo crear la materia prima.',
          });
        }
      }
      setCatalogTick((n) => n + 1);
      await refreshRemote?.().catch(() => null);
      const data = {
        created,
        skipped,
        errors: errorRows.length,
        error_rows: errorRows,
      };
      setBulkImportResult(data);
      if (errorRows.length === 0 || created > 0) {
        setBulkImportNotice(
          `Importación exitosa: ${created} materia(s) prima(s) creadas` +
            (skipped ? `, ${skipped} omitidas` : '') +
            '.'
        );
      }
      return data;
    } catch (error) {
      const fail = {
        created,
        skipped,
        errors: 1,
        error_rows: [{ row: 0, detail: error.message || 'No se pudo importar.' }],
      };
      setBulkImportResult(fail);
      throw error;
    } finally {
      setIsImportingMaterials(false);
    }
  };

  const openCreateFromReception = (row) => {
    const procurementCode = String(row?.procurement_code || '').trim().toUpperCase();
    const option = procurementOptions.find((item) => item.value === procurementCode);
    const family = familyForProcurementType(
      option?.item_type || row?.procurement_type || '',
      'Extendedores'
    );
    setFormData({
      ...emptyMaterial(),
      family,
      code: nextMaterialCode(materials, family),
      name: row?.procurement_name || row?.material_name || option?.name || '',
      unit: row?.unit || option?.unit || 'kg',
      typical_supplier: row?.supplier || '',
      procurement_item_id: row?.procurement_item_id || option?.id || null,
      procurement_code: procurementCode,
      procurement_name: row?.procurement_name || option?.name || '',
      procurement_type: option?.item_type || '',
      tech_params: [emptyTechParam()],
    });
    setIsEditing(false);
    setFormError(
      procurementCode
        ? `Crea el SKU de producción para ${procurementCode} antes de recepcionar.`
        : 'Crea el SKU de producción antes de recepcionar.'
    );
    setShowForm(true);
    setShowReception(false);
    setDetail(null);
  };

  const openEdit = (row) => {
    setFormData({ ...emptyMaterial(), ...row });
    setIsEditing(true);
    setFormError('');
    setShowForm(true);
    setDetail(null);
  };

  const handleChange = (key, value) => {
    setFormData((current) => {
      const next = { ...current, [key]: value };
      if (key === 'family') {
        const others = isEditing ? materials.filter((row) => row.id !== current.id) : materials;
        next.code = nextMaterialCode(others, value);
      }
      if (key === 'code') {
        next.code = String(value || '').toUpperCase();
      }
      if (key === 'procurement_code') {
        const code = String(value || '').trim().toUpperCase();
        const option = procurementOptions.find((item) => item.value === code);
        next.procurement_code = code;
        next.procurement_item_id = option?.id || null;
        next.procurement_name = option?.name || '';
        next.procurement_type = option?.item_type || current.procurement_type || '';
      }
      return next;
    });
  };

  const handleSave = async () => {
    const name = String(formData.name || '').trim();
    if (!name) {
      setFormError('Indica el nombre de la materia prima.');
      return;
    }
    const family = formData.family || 'Resinas';
    const code = (
      String(formData.code || '').trim() || nextMaterialCode(materials, family)
    ).toUpperCase();
    if (!isMaterialCode(code)) {
      setFormError('El código debe tener 2 letras de categoría y 4 dígitos correlativos (ej. RS0001).');
      return;
    }
    const expectedPrefix = getMaterialCategoryPrefixMap()[family];
    if (expectedPrefix && expectedPrefix !== code.slice(0, 2)) {
      setFormError(
        `El código ${code} no corresponde a ${family} (debe comenzar con ${expectedPrefix}).`
      );
      return;
    }
    if (!expectedPrefix) {
      // Categoría nueva aún no persistida: registra el prefijo del código.
      const created = upsertMaterialCategory({ name: family, prefix: code.slice(0, 2) });
      if (!created.ok) {
        setFormError(created.error || 'No se pudo registrar la categoría.');
        return;
      }
      setCatalogTick((n) => n + 1);
    }
    const duplicate = materials.some(
      (row) => row.code === code && Number(row.id) !== Number(formData.id)
    );
    if (duplicate) {
      setFormError('Ya existe una materia prima con ese código.');
      return;
    }
    const procurementCode = String(formData.procurement_code || '').trim().toUpperCase();
    if (procurementCode) {
      const linked = materials.some(
        (row) =>
          String(row.procurement_code || '').trim().toUpperCase() === procurementCode &&
          Number(row.id) !== Number(formData.id)
      );
      if (linked) {
        setFormError(`El código de adquisiciones ${procurementCode} ya está vinculado a otra materia prima.`);
        return;
      }
    }
    const techParams = sanitizeTechParams(formData.tech_params);
    const rangeError = techParamRangeError(techParams);
    if (rangeError) {
      setFormError(rangeError);
      return;
    }
    techParams.forEach((param) => {
      upsertTechParamSuggestion({ name: param.name, unit: param.unit });
    });
    setCatalogTick((n) => n + 1);
    const catalogMatch = procurementCode
      ? procurementOptions.find((item) => item.value === procurementCode)
      : null;
    const payload = {
      ...formData,
      code,
      name,
      family,
      procurement_item_id: catalogMatch?.id || null,
      procurement_code: procurementCode,
      procurement_name: catalogMatch?.name || '',
      procurement_type: catalogMatch?.item_type || '',
      tech_params: techParams,
      unit_cost: Number(formData.unit_cost || 0),
      min_stock: Number(formData.min_stock || 0),
      has_sds: Boolean(formData.has_sds),
    };
    setIsSaving(true);
    try {
      await saveMaterialRecord(payload, isEditing ? formData.id : null);
      setShowForm(false);
    } catch (err) {
      setFormError(err?.message || 'No se pudo guardar la materia prima.');
    } finally {
      setIsSaving(false);
    }
  };

  const openReceive = (row = null) => {
    const base = emptyReception();
    if (row && isPendingReception(row)) {
      if (row.needs_production_sku || !row.material_code) {
        openCreateFromReception(row);
        return;
      }
      const material =
        materials.find(
          (item) => item.id === row.material_id || item.code === row.material_code
        ) || null;
      const materialCode = material?.code || row.material_code || '';
      setReceptionForm({
        ...base,
        reception_id: row.id || null,
        purchase_receipt_id: row.purchase_receipt_id || null,
        purchase_order_id: row.purchase_order_id || null,
        material_id: material?.id || row.material_id || null,
        material_code: materialCode,
        date: row.date || base.date,
        quantity: row.quantity ?? '',
        unit: row.unit || material?.unit || 'kg',
        warehouse:
          row.warehouse && MP_WAREHOUSES.includes(row.warehouse)
            ? row.warehouse
            : material?.storage && MP_WAREHOUSES.includes(material.storage)
              ? material.storage
              : 'Bodega MP',
        supplier: row.supplier || material?.typical_supplier || '',
        lot_code: nextLotCode(lots, materialCode, row.date || base.date),
        oc_code: row.oc_code || '',
        batch_params: batchParamsFromMaterial(material),
      });
    } else if (row?.code) {
      const material =
        materials.find((item) => item.id === row.id || item.code === row.code) || row;
      setReceptionForm({
        ...base,
        material_id: material.id,
        material_code: material.code,
        unit: material.unit || 'kg',
        warehouse: material.storage && MP_WAREHOUSES.includes(material.storage)
          ? material.storage
          : 'Bodega MP',
        supplier: material.typical_supplier || '',
        lot_code: nextLotCode(lots, material.code, base.date),
        batch_params: batchParamsFromMaterial(material),
      });
    } else {
      setReceptionForm(base);
    }
    setReceptionError('');
    setShowReception(true);
    setShowForm(false);
    setDetail(null);
  };

  const handleReceptionChange = (key, value) => {
    setReceptionForm((current) => {
      const next = { ...current, [key]: value };
      if (key === 'material_code') {
        const material = materials.find((item) => item.code === value);
        next.material_id = material?.id || null;
        next.unit = material?.unit || current.unit || 'kg';
        next.warehouse = MP_WAREHOUSES.includes(material?.storage)
          ? material.storage
          : current.warehouse || 'Bodega MP';
        next.supplier = current.supplier || material?.typical_supplier || '';
        next.lot_code = material ? nextLotCode(lots, material.code, current.date) : '';
        next.batch_params = batchParamsFromMaterial(material);
      }
      if (key === 'date' && current.material_code) {
        next.lot_code = nextLotCode(lots, current.material_code, value);
      }
      return next;
    });
  };

  const handleSaveReception = async () => {
    const material = materials.find((item) => item.code === receptionForm.material_code);
    if (!material) {
      setReceptionError('Selecciona la materia prima a recepcionar.');
      return;
    }
    const quantity = Number(String(receptionForm.quantity ?? '').replace(',', '.'));
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setReceptionError('Indica la cantidad recepcionada.');
      return;
    }
    setIsSavingReception(true);
    try {
      await saveReceptionRecord({
        reception_id: receptionForm.reception_id || null,
        purchase_receipt_id: receptionForm.purchase_receipt_id || null,
        purchase_order_id: receptionForm.purchase_order_id || null,
        material_id: material.id,
        material_code: material.code,
        material_name: material.name,
        movement_date: receptionForm.date,
        date: receptionForm.date,
        quantity,
        unit: receptionForm.unit || material.unit || 'kg',
        warehouse: receptionForm.warehouse || 'Bodega MP',
        lot_code: String(receptionForm.lot_code || '').trim().toUpperCase(),
        supplier: receptionForm.supplier || material.typical_supplier || '',
        supplier_lot: receptionForm.supplier_lot || '',
        expiry: receptionForm.expiry || null,
        oc_code: receptionForm.oc_code || '',
        batch_params: receptionForm.batch_params || [],
      });
      setShowReception(false);
    } catch (err) {
      setReceptionError(err?.message || 'No se pudo guardar la recepción.');
    } finally {
      setIsSavingReception(false);
    }
  };

  const openCreatePurchaseRequest = (itemsFromCart = null) => {
    const cartItems = Array.isArray(itemsFromCart) ? itemsFromCart : null;
    setIsEditingPurchaseRequest(false);
    setEditingPurchaseRequestId(null);
    setPurchaseRequestFromCart(Boolean(cartItems?.length));
    setPurchaseRequestError('');
    setPurchaseRequestForm({
      ...emptyPurchaseRequest(),
      items: cartItems?.length
        ? cartItems.map((item) => ({
            id: null,
            item_code: item.item_code || '',
            description: item.description || '',
            quantity: Number(item.quantity || 1),
            unit: item.unit || 'UN',
            estimated_unit_price: Number(item.estimated_unit_price || 0),
          }))
        : emptyPurchaseRequest().items,
    });
    setShowPurchaseRequest(true);
    setPurchaseRequestDetail(null);
  };

  const addMaterialToCart = () => {
    const code = String(cartMaterialCode || '').trim().toUpperCase();
    const material = materials.find((row) => String(row.code || '').toUpperCase() === code);
    if (!material) {
      window.alert('Selecciona una materia prima del catálogo.');
      return;
    }
    const quantity = Number(cartQuantity || 0);
    if (!(quantity > 0)) {
      window.alert('Indica una cantidad mayor a 0.');
      return;
    }
    const unitCost = Number(material.unit_cost || catalogByCode[material.procurement_code]?.unit_cost || 0);
    const next = [...purchaseCart];
    const existing = next.findIndex((row) => String(row.item_code || '').toUpperCase() === code);
    if (existing >= 0) {
      next[existing] = {
        ...next[existing],
        quantity: Number(next[existing].quantity || 0) + quantity,
      };
    } else {
      next.push({
        id: `cart-${code}-${Date.now()}`,
        item_code: material.code,
        description: material.name || material.code,
        quantity,
        unit: material.unit || 'kg',
        estimated_unit_price: unitCost,
        family: material.family || '',
      });
    }
    persistPurchaseCart(next);
    setCartMaterialCode('');
    setCartQuantity('1');
  };

  const updateCartLine = (lineId, patch) => {
    persistPurchaseCart(
      purchaseCart.map((row) => (row.id === lineId ? { ...row, ...patch } : row))
    );
  };

  const removeCartLine = (lineId) => {
    persistPurchaseCart(purchaseCart.filter((row) => row.id !== lineId));
  };

  const clearPurchaseCart = () => {
    if (!purchaseCart.length) return;
    if (!window.confirm('¿Vaciar el carro de compra?')) return;
    persistPurchaseCart([]);
  };

  const createRequestFromCart = () => {
    if (!purchaseCart.length) {
      window.alert('El carro está vacío.');
      return;
    }
    openCreatePurchaseRequest(purchaseCart);
  };

  const openPurchaseRequestDetail = async (row) => {
    const requestId = Number(row?.purchase_request_id || row?.id || 0);
    if (!requestId) return;
    try {
      const full = await purchaseRequestsAPI.getById(requestId);
      setPurchaseRequestDetail(full);
    } catch (err) {
      window.alert(err?.message || 'No se pudo cargar la solicitud.');
    }
  };

  const openEditPurchaseRequest = async (request) => {
    const source = request?.items ? request : await purchaseRequestsAPI.getById(request.id);
    setPurchaseRequestDetail(null);
    setIsEditingPurchaseRequest(true);
    setEditingPurchaseRequestId(source.id);
    setPurchaseRequestError('');
    setPurchaseRequestForm({
      request_code: source.request_code || '',
      requester_name: source.requester_name || '',
      department: source.department || PRODUCTION_PURCHASE_AREA,
      supplier_id: source.supplier_id ? String(source.supplier_id) : '',
      request_date: source.request_date ? String(source.request_date).slice(0, 10) : todayIso(),
      needed_by_date: source.needed_by_date ? String(source.needed_by_date).slice(0, 10) : '',
      priority: source.priority || 'Normal',
      status: source.status || 'Borrador',
      justification: source.justification || '',
      notes: source.notes || '',
      items:
        Array.isArray(source.items) && source.items.length
          ? source.items.map((item) => ({
              id: item.id,
              item_code: item.item_code || '',
              description: item.description || '',
              quantity: item.quantity ?? 1,
              unit: item.unit || 'UN',
              estimated_unit_price: item.estimated_unit_price ?? 0,
            }))
          : emptyPurchaseRequest().items,
    });
    setShowPurchaseRequest(true);
  };

  const handlePurchaseRequestChange = (key, value) => {
    setPurchaseRequestForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSavePurchaseRequest = async () => {
    const requester = String(purchaseRequestForm.requester_name || '').trim();
    if (!requester) {
      setPurchaseRequestError('El solicitante es obligatorio.');
      return;
    }
    const items = (purchaseRequestForm.items || [])
      .map((item) => ({
        id: item.id || null,
        item_code: String(item.item_code || '').trim(),
        description: String(item.description || '').trim(),
        quantity: Number(item.quantity || 0),
        unit: String(item.unit || 'UN').trim() || 'UN',
        estimated_unit_price: Number(item.estimated_unit_price || 0),
      }))
      .filter((item) => item.description || item.item_code);
    if (!items.length) {
      setPurchaseRequestError('Agrega al menos un ítem con descripción.');
      return;
    }

    const payload = {
      requester_name: requester,
      department: PRODUCTION_PURCHASE_AREA,
      supplier_id: Number(purchaseRequestForm.supplier_id || 0) || null,
      request_date: purchaseRequestForm.request_date || todayIso(),
      needed_by_date: purchaseRequestForm.needed_by_date || null,
      priority: String(purchaseRequestForm.priority || 'Normal').trim() || 'Normal',
      status: String(purchaseRequestForm.status || 'Borrador').trim() || 'Borrador',
      justification: String(purchaseRequestForm.justification || '').trim(),
      notes: String(purchaseRequestForm.notes || '').trim(),
      items,
    };

    setIsSavingPurchaseRequest(true);
    setPurchaseRequestError('');
    try {
      if (isEditingPurchaseRequest && editingPurchaseRequestId) {
        await purchaseRequestsAPI.update(editingPurchaseRequestId, payload);
      } else {
        await purchaseRequestsAPI.create(payload);
      }
      setShowPurchaseRequest(false);
      setEditingPurchaseRequestId(null);
      setPurchaseRequestForm(emptyPurchaseRequest());
      if (purchaseRequestFromCart) {
        persistPurchaseCart([]);
        setPurchaseRequestFromCart(false);
        setComprasView('solicitudes');
      }
      await loadPurchaseRequests();
      notifyPurchaseRequestsChanged();
    } catch (err) {
      setPurchaseRequestError(err?.message || 'No se pudo guardar la solicitud.');
    } finally {
      setIsSavingPurchaseRequest(false);
    }
  };

  const [selectedPurchaseRequestIds, setSelectedPurchaseRequestIds] = useState([]);

  const handleBulkDeletePurchaseRequests = async () => {
    const ids = selectedPurchaseRequestIds.map(Number).filter(Boolean);
    if (!ids.length) return;
    const ok = window.confirm(
      ids.length === 1 ? '¿Eliminar esta solicitud?' : `¿Eliminar ${ids.length} solicitudes?`
    );
    if (!ok) return;
    try {
      await purchaseRequestsAPI.bulkDelete(ids);
      setSelectedPurchaseRequestIds([]);
      await loadPurchaseRequests();
      notifyPurchaseRequestsChanged();
    } catch (err) {
      window.alert(err?.message || 'No se pudieron eliminar las solicitudes.');
    }
  };

  const receptionSections = [
    {
      key: 'receipt',
      title: 'Recepción',
      columns: 3,
      fields: [
        {
          key: 'material_code',
          label: 'Materia prima',
          type: 'select',
          options: [
            { value: '', label: 'Seleccionar ficha…' },
            ...materials.map((item) => ({
              value: item.code,
              label: `${item.code} · ${item.name}`,
            })),
          ],
        },
        { key: 'date', label: 'Fecha', type: 'date' },
        { key: 'quantity', label: 'Cantidad', type: 'number' },
        { key: 'unit', label: 'Unidad', type: 'select', options: MATERIAL_UNITS.map((item) => ({ value: item, label: item })) },
        {
          key: 'warehouse',
          label: 'Bodega',
          type: 'select',
          options: MP_WAREHOUSES.map((item) => ({ value: item, label: item })),
        },
        { key: 'lot_code', label: 'Lote interno', placeholder: 'L-RS0001-2608-01' },
        { key: 'supplier_lot', label: 'Lote proveedor' },
        { key: 'expiry', label: 'Vencimiento', type: 'date' },
        { key: 'supplier', label: 'Proveedor' },
        { key: 'oc_code', label: 'OC (opcional)', placeholder: '26001' },
      ],
    },
    {
      key: 'batch',
      title: 'Parámetros del lote',
      columns: 1,
      fields: [
        {
          key: 'batch_params',
          type: 'custom',
          fullWidth: true,
          render: ({ formData: data, onChange, colors, baseInputStyle }) => (
            <BatchParamsTable
              rows={data.batch_params || []}
              onChange={onChange}
              colors={colors}
              baseInputStyle={baseInputStyle}
            />
          ),
        },
      ],
    },
  ];

  const sections = [
    {
      key: 'identity',
      title: 'Identificación',
      columns: 3,
      fields: [
        {
          key: 'family',
          label: 'Categoría',
          type: 'custom',
          render: ({ formData: data, onChange, colors, baseInputStyle }) => (
            <FamilySelectField
              value={data.family || 'Resinas'}
              onChange={(next) => onChange(next)}
              colors={colors}
              baseInputStyle={baseInputStyle}
              families={materialFamilies}
              onCatalogChange={() => setCatalogTick((n) => n + 1)}
              isDarkMode={isDarkMode}
            />
          ),
        },
        {
          key: 'code',
          label: 'Código producción',
          placeholder: nextMaterialCode(materials, formData.family || 'Resinas'),
        },
        {
          key: 'procurement_code',
          label: 'Código adquisiciones',
          type: 'select',
          options: procurementOptions,
        },
        { key: 'name', label: 'Nombre comercial', placeholder: 'Nombre interno de planta' },
        { key: 'chemical_name', label: 'Nombre químico', placeholder: 'Denominación IUPAC o habitual' },
        { key: 'cas_number', label: 'N° CAS', placeholder: '25068-38-6' },
        { key: 'appearance', label: 'Apariencia', placeholder: 'Líquido viscoso ámbar' },
        {
          key: 'hazard_class',
          label: 'Clase de peligro',
          type: 'select',
          options: HAZARD_CLASSES.map((item) => ({ value: item, label: item })),
        },
        {
          key: 'status',
          label: 'Estado',
          type: 'select',
          options: MATERIAL_STATUSES.map((item) => ({ value: item, label: item })),
        },
      ],
    },
    {
      key: 'tech',
      title: 'Parámetros técnicos',
      columns: 1,
      fields: [
        {
          key: 'tech_params',
          type: 'custom',
          fullWidth: true,
          render: ({ formData: data, onChange, colors, baseInputStyle }) => (
            <TechParamsTable
              rows={data.tech_params || []}
              onChange={onChange}
              colors={colors}
              baseInputStyle={baseInputStyle}
              suggestions={techParamSuggestions}
              onSuggestionsChange={() => setCatalogTick((n) => n + 1)}
            />
          ),
        },
      ],
    },
    {
      key: 'supply',
      title: 'Abastecimiento',
      columns: 3,
      defaultOpen: false,
      fields: [
        { key: 'unit', label: 'Unidad', type: 'select', options: MATERIAL_UNITS.map((item) => ({ value: item, label: item })) },
        { key: 'unit_cost', label: 'Costo unitario (CLP)', type: 'number' },
        { key: 'min_stock', label: 'Stock mínimo', type: 'number' },
        { key: 'typical_supplier', label: 'Proveedor habitual' },
        { key: 'packing', label: 'Envase / presentación' },
        { key: 'storage', label: 'Almacenamiento', colSpan: 2 },
        { key: 'notes', label: 'Notas', type: 'textarea', fullWidth: true, rows: 3 },
      ],
    },
  ];

  const sectionTabs = [
    {
      key: 'catalogo',
      label: 'Catálogo',
      icon: <FlaskConical size={16} />,
      content: (
        <div style={{ display: 'grid', gap: 16 }}>
          <RawMaterialsInventoryChart
            materials={materials}
            lots={lots}
            ingresos={ingresos}
            egresos={egresos}
            catalogByCode={catalogByCode}
          />
          {bulkImportNotice ? (
            <div
              style={{
                marginBottom: 10,
                padding: '10px 12px',
                borderRadius: 8,
                border: `1px solid ${ui.border}`,
                background: ui.isDarkMode ? 'rgba(22,163,74,0.12)' : '#f0fdf4',
                color: ui.text,
                fontSize: 13,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                alignItems: 'center',
              }}
            >
              <span>{bulkImportNotice}</span>
              <button
                type="button"
                onClick={() => setBulkImportNotice('')}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: ui.muted,
                  cursor: 'pointer',
                  fontSize: 12,
                }}
              >
                Cerrar
              </button>
            </div>
          ) : null}
          <MaterialsTable
            materials={materials}
            lots={lots}
            receptions={receptions}
            ingresos={ingresos}
            egresos={egresos}
            inventoryCounts={inventoryCounts}
            catalogByCode={catalogByCode}
            onCreate={openCreate}
            onBulkImport={() => {
              setBulkImportResult(null);
              setShowBulkImportModal(true);
            }}
            onBulkImportTemplate={downloadRawMaterialsExcelTemplate}
            onEdit={openEdit}
            onOpen={setDetail}
            onReceive={openReceive}
            onCreateReception={() => openReceive(null)}
            onCreateSkuFromReception={openCreateFromReception}
            onCreateInventoryCount={() => {
              setInventoryCountId(null);
              setShowInventoryCount(true);
            }}
            onOpenInventoryCount={(row) => {
              setInventoryCountId(row?.id || null);
              setShowInventoryCount(true);
            }}
          />
        </div>
      ),
    },
    {
      key: 'compras',
      label: 'Compras',
      icon: <ShoppingCart size={16} />,
      badge: purchaseRequests.length || undefined,
      content: (() => {
        const comprasTabs = [
          { key: 'solicitudes', label: 'Solicitudes', count: purchaseRequests.length },
          { key: 'items', label: 'Ítems solicitados', count: purchaseRequestItems.length },
          { key: 'carro', label: 'Carro', count: purchaseCart.length },
        ];
        const cartTotal = purchaseCart.reduce(
          (sum, row) => sum + Number(row.quantity || 0) * Number(row.estimated_unit_price || 0),
          0
        );
        const views = {
          solicitudes: {
            title: 'Solicitudes de compra · Producción',
            subtitle: `${purchaseRequests.length} solicitudes de materias primas`,
            rows: purchaseRequests,
            searchPlaceholder: 'Buscar código, solicitante, proveedor…',
            defaultSortKey: 'request_date',
            onRowClick: openPurchaseRequestDetail,
            selectable: true,
            action: (
              <button type="button" onClick={() => openCreatePurchaseRequest()} style={primaryBtn}>
                <Plus size={14} /> Nueva solicitud
              </button>
            ),
            columns: [
              { key: 'Código', sortKey: 'request_code' },
              { key: 'Fecha', sortKey: 'request_date' },
              { key: 'Solicitante', sortKey: 'requester_name' },
              { key: 'Prioridad', sortKey: 'priority' },
              { key: 'Proveedor', sortKey: 'supplier_name' },
              { key: 'Necesario para', sortKey: 'needed_by_date' },
              { key: 'Total est.', sortKey: 'estimated_total', align: 'right' },
              { key: 'Estado', sortKey: 'status', align: 'center' },
            ],
            renderCells: (row) => (
              <>
                {cell(<strong>{row.request_code || '—'}</strong>)}
                {cell(formatDateEs(row.request_date))}
                {cell(row.requester_name || '—')}
                {cell(row.priority || '—')}
                {cell(row.supplier_name || '—')}
                {cell(formatDateEs(row.needed_by_date))}
                {cell(formatMoneyClp(row.estimated_total), 'right')}
                {cell(
                  <ProductionStatusChip
                    status={row.status}
                    color={PURCHASE_REQUEST_STATUS_COLORS[row.status] || '#64748b'}
                  />,
                  'center'
                )}
              </>
            ),
          },
          items: {
            title: 'Ítems solicitados · Producción',
            subtitle: `${purchaseRequestItems.length} líneas en solicitudes de materias primas`,
            rows: purchaseRequestItems,
            searchPlaceholder: 'Buscar solicitud, código, descripción…',
            defaultSortKey: 'request_date',
            onRowClick: openPurchaseRequestDetail,
            selectable: false,
            action: null,
            columns: [
              { key: 'Solicitud', sortKey: 'request_code' },
              { key: 'Código', sortKey: 'item_code' },
              { key: 'Descripción', sortKey: 'description' },
              { key: 'Cantidad', sortKey: 'quantity', align: 'right' },
              { key: 'P. unit.', sortKey: 'estimated_unit_price', align: 'right' },
              { key: 'Total', sortKey: 'line_total', align: 'right' },
              { key: 'Solicitante', sortKey: 'requester_name' },
              { key: 'Estado', sortKey: 'request_status', align: 'center' },
            ],
            renderCells: (row) => (
              <>
                {cell(<strong>{row.request_code || '—'}</strong>)}
                {cell(row.item_code || '—')}
                {cell(row.description || '—')}
                {cell(formatQty(row.quantity, row.unit), 'right')}
                {cell(formatMoneyClp(row.estimated_unit_price), 'right')}
                {cell(formatMoneyClp(row.line_total), 'right')}
                {cell(row.requester_name || '—')}
                {cell(
                  <ProductionStatusChip
                    status={row.request_status}
                    color={PURCHASE_REQUEST_STATUS_COLORS[row.request_status] || '#64748b'}
                  />,
                  'center'
                )}
              </>
            ),
          },
          carro: {
            title: 'Carro de compra · Producción',
            subtitle: purchaseCart.length
              ? `${purchaseCart.length} ítem(s) · estimado ${formatMoneyClp(cartTotal)}`
              : 'Sin ítems en el carro',
            rows: purchaseCart,
            searchPlaceholder: 'Buscar código o descripción…',
            defaultSortKey: 'item_code',
            onRowClick: undefined,
            selectable: false,
            action: (
              <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={clearPurchaseCart}
                  disabled={!purchaseCart.length}
                  style={{
                    ...primaryBtn,
                    background: 'transparent',
                    color: ui.text,
                    border: `1px solid ${ui.border}`,
                    opacity: purchaseCart.length ? 1 : 0.45,
                  }}
                >
                  Vaciar
                </button>
                <button
                  type="button"
                  onClick={createRequestFromCart}
                  disabled={!purchaseCart.length}
                  style={{ ...primaryBtn, opacity: purchaseCart.length ? 1 : 0.45 }}
                >
                  <Plus size={14} /> Crear solicitud
                </button>
              </div>
            ),
            columns: [
              { key: 'Código', sortKey: 'item_code' },
              { key: 'Descripción', sortKey: 'description' },
              { key: 'Cantidad', sortKey: 'quantity', align: 'right' },
              { key: 'Unidad', sortKey: 'unit' },
              { key: 'P. unit. est.', sortKey: 'estimated_unit_price', align: 'right' },
              { key: 'Total est.', sortKey: 'line_total', align: 'right' },
              { key: '', sortKey: null, align: 'right' },
            ],
            renderCells: (row) => (
              <>
                {cell(<strong style={{ fontVariantNumeric: 'tabular-nums' }}>{row.item_code || '—'}</strong>)}
                {cell(row.description || '—')}
                {cell(
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={row.quantity}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) =>
                      updateCartLine(row.id, { quantity: Number(event.target.value || 0) })
                    }
                    style={{
                      width: 88,
                      height: 30,
                      borderRadius: 6,
                      border: `1px solid ${ui.inputBorder}`,
                      background: ui.inputBg,
                      color: ui.text,
                      padding: '0 8px',
                      fontSize: 12,
                      textAlign: 'right',
                    }}
                  />,
                  'right'
                )}
                {cell(row.unit || '—')}
                {cell(formatMoneyClp(row.estimated_unit_price), 'right')}
                {cell(formatMoneyClp(Number(row.quantity || 0) * Number(row.estimated_unit_price || 0)), 'right')}
                {cell(
                  <button
                    type="button"
                    title="Quitar del carro"
                    aria-label={`Quitar ${row.item_code}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      removeCartLine(row.id);
                    }}
                    style={iconBtn(ui)}
                  >
                    <Trash2 size={14} />
                  </button>,
                  'right'
                )}
              </>
            ),
          },
        };
        const current = views[comprasView] || views.solicitudes;
        return (
          <div style={{ display: 'grid', gap: 12 }}>
            {comprasView === 'carro' ? (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  alignItems: 'flex-end',
                  padding: '10px 12px',
                  border: `1px solid ${ui.border}`,
                  borderRadius: 10,
                  background: ui.header,
                }}
              >
                <label style={{ display: 'grid', gap: 4, fontSize: 11, fontWeight: 600, color: ui.muted, flex: '1 1 260px' }}>
                  Materia prima
                  <select
                    value={cartMaterialCode}
                    onChange={(event) => setCartMaterialCode(event.target.value)}
                    style={{
                      height: 36,
                      borderRadius: 8,
                      border: `1px solid ${ui.inputBorder}`,
                      background: ui.inputBg,
                      color: ui.text,
                      padding: '0 10px',
                      fontSize: 13,
                    }}
                  >
                    <option value="">Seleccionar del catálogo…</option>
                    {materials.map((row) => (
                      <option key={row.code} value={row.code}>
                        {row.code} · {row.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={{ display: 'grid', gap: 4, fontSize: 11, fontWeight: 600, color: ui.muted }}>
                  Cantidad
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={cartQuantity}
                    onChange={(event) => setCartQuantity(event.target.value)}
                    style={{
                      height: 36,
                      width: 110,
                      borderRadius: 8,
                      border: `1px solid ${ui.inputBorder}`,
                      background: ui.inputBg,
                      color: ui.text,
                      padding: '0 10px',
                      fontSize: 13,
                      boxSizing: 'border-box',
                    }}
                  />
                </label>
                <button type="button" onClick={addMaterialToCart} style={primaryBtn}>
                  <Plus size={14} /> Agregar al carro
                </button>
              </div>
            ) : null}
            <ProductionDataTable
              title={current.title}
              subtitle={current.subtitle}
              rows={current.rows}
              columns={current.columns}
              renderCells={current.renderCells}
              searchPlaceholder={current.searchPlaceholder}
              defaultSortKey={current.defaultSortKey}
              defaultSortDir="desc"
              onRowClick={current.onRowClick}
              action={current.action}
              tabs={comprasTabs}
              activeTab={comprasView}
              onTabChange={setComprasView}
              selectable={Boolean(current.selectable)}
              selectedIds={selectedPurchaseRequestIds}
              onSelectionChange={setSelectedPurchaseRequestIds}
              bulkActions={
                current.selectable ? (
                  <button
                    type="button"
                    onClick={handleBulkDeletePurchaseRequests}
                    style={{
                      ...primaryBtn,
                      background: '#dc2626',
                      height: 30,
                      fontSize: 12,
                    }}
                  >
                    <Trash2 size={14} /> Eliminar
                  </button>
                ) : null
              }
            />
          </div>
        );
      })(),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Heading level={2} isDarkMode={isDarkMode} style={{ marginBottom: 6 }}>
          Materias primas
        </Heading>
        <p style={{ margin: 0, maxWidth: 720, fontSize: 13, color: ui.muted, lineHeight: 1.45 }}>
          Catálogo e inventario de planta, cierre de mes y solicitudes de compra de materias primas
          del área Producción.
        </p>
      </div>

      <TabNavigation tabs={sectionTabs} defaultTab="catalogo" isDarkMode={isDarkMode} />

      <FormDrawer
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onSave={handleSave}
        isSaving={isSaving}
        isDarkMode={isDarkMode}
        title={isEditing ? 'Editar materia prima' : 'Nueva materia prima'}
        subtitle={formData.family || 'Ficha de catálogo'}
        sections={sections}
        formData={formData}
        onChange={handleChange}
        formError={formError}
        saveLabel={isEditing ? 'Guardar cambios' : 'Crear ficha'}
        width={760}
      />

      <FormDrawer
        isOpen={showReception}
        onClose={() => setShowReception(false)}
        onSave={handleSaveReception}
        isSaving={isSavingReception}
        isDarkMode={isDarkMode}
        title="Recepcionar materia prima"
        subtitle={
          receptionForm.material_code
            ? `${receptionForm.material_code} · lote ${receptionForm.lot_code || 'nuevo'}`
            : 'Ingreso a bodega de planta'
        }
        sections={receptionSections}
        formData={receptionForm}
        onChange={handleReceptionChange}
        formError={receptionError}
        saveLabel="Confirmar recepción"
        width={760}
      />

      <PaintMaterialDetailModal
        isOpen={Boolean(detail)}
        onClose={() => setDetail(null)}
        material={detail}
        lots={detail ? lotsForMaterial(lots, detail.code) : []}
        isDarkMode={isDarkMode}
        ui={ui}
        onEdit={openEdit}
        onReceive={openReceive}
      />

      <PurchaseRequestsForm
        isOpen={showPurchaseRequest}
        onClose={() => !isSavingPurchaseRequest && setShowPurchaseRequest(false)}
        onSave={handleSavePurchaseRequest}
        isSaving={isSavingPurchaseRequest}
        isDarkMode={isDarkMode}
        isEditing={isEditingPurchaseRequest}
        formData={purchaseRequestForm}
        onChange={handlePurchaseRequestChange}
        formError={purchaseRequestError}
        supplierOptions={supplierOptions}
        areaOptions={purchaseRequestAreaOpts}
      />

      <PurchaseRequestDetailModal
        isOpen={Boolean(purchaseRequestDetail)}
        onClose={() => setPurchaseRequestDetail(null)}
        purchaseRequest={purchaseRequestDetail}
        isDarkMode={isDarkMode}
        onEdit={openEditPurchaseRequest}
      />

      <RawMaterialsMonthEndInventoryModal
        isOpen={showInventoryCount}
        countId={inventoryCountId}
        onClose={() => setShowInventoryCount(false)}
        onSaved={async (payload) => {
          if (payload?.id) setInventoryCountId(payload.id);
          try {
            await refreshRemote?.();
          } catch {
            /* keep local modal state */
          }
        }}
      />

      <RawMaterialsBulkImportModal
        isOpen={showBulkImportModal}
        onClose={() => {
          if (isImportingMaterials) return;
          setShowBulkImportModal(false);
          setBulkImportResult(null);
        }}
        onImport={handleBulkImportMaterials}
        isImporting={isImportingMaterials}
        isDarkMode={isDarkMode}
        result={bulkImportResult}
      />
    </div>
  );
};

export default PaintRawMaterialsWorkspace;
